import {
  type FieldLeadDatabase,
  type FieldLeadRecord,
  openFieldLeadDb,
  readAllRecords,
  readRecord,
  removeRecord,
  writeRecord,
} from "./historyDb";

const EMPTY: FieldLeadRecord[] = [];

let connection: Promise<FieldLeadDatabase> | null = null;
let owner: string | null = null;
let records: FieldLeadRecord[] = EMPTY;
const listeners = new Set<() => void>();

function database(): Promise<FieldLeadDatabase> {
  if (!connection) {
    connection = openFieldLeadDb();
  }
  return connection;
}

function byNewestFirst(items: FieldLeadRecord[]): FieldLeadRecord[] {
  return [...items].sort((left, right) => (left.loadedAt < right.loadedAt ? 1 : -1));
}

function publish(): void {
  for (const listener of listeners) {
    listener();
  }
}

export async function initFieldLeadHistory(ownerKey: string): Promise<void> {
  const db = await database();
  const stored = await readAllRecords(db);
  for (const record of stored) {
    if (record.ownerKey !== ownerKey) {
      await removeRecord(db, record.clientLoadId);
    }
  }
  owner = ownerKey;
  records = byNewestFirst(stored.filter((record) => record.ownerKey === ownerKey));
  publish();
}

export async function rememberFieldLead(record: FieldLeadRecord): Promise<void> {
  const db = await database();
  await writeRecord(db, record);
  if (record.ownerKey !== owner) {
    return;
  }
  records = byNewestFirst([
    ...records.filter((kept) => kept.clientLoadId !== record.clientLoadId),
    record,
  ]);
  publish();
}

export async function markFieldLeadSent(clientLoadId: string, leadId: string): Promise<void> {
  const db = await database();
  const stored = await readRecord(db, clientLoadId);
  if (stored === null || stored.delivery === "sent") {
    return;
  }
  const sent: FieldLeadRecord = { ...stored, delivery: "sent", leadId };
  await writeRecord(db, sent);
  if (sent.ownerKey !== owner) {
    return;
  }
  records = byNewestFirst([...records.filter((kept) => kept.clientLoadId !== clientLoadId), sent]);
  publish();
}

export async function forgetFieldLead(clientLoadId: string): Promise<void> {
  const db = await database();
  await removeRecord(db, clientLoadId);
  records = records.filter((record) => record.clientLoadId !== clientLoadId);
  publish();
}

export async function isFieldLeadRemembered(clientLoadId: string): Promise<boolean> {
  const db = await database();
  return (await readRecord(db, clientLoadId)) !== null;
}

export function fieldLeadHistory(): FieldLeadRecord[] {
  return records;
}

export function subscribeToFieldLeadHistory(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export async function closeFieldLeadHistory(): Promise<void> {
  const open = connection;
  connection = null;
  owner = null;
  records = EMPTY;
  publish();
  if (open) {
    (await open).close();
  }
}
